import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Check, Copy, Share2 } from 'lucide-react';

interface ReferralSuccessCardProps {
  rewardAmount: number;
  referralCode: string;
}

export function ReferralSuccessCard({ rewardAmount, referralCode }: ReferralSuccessCardProps) {
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);

  const referralLink = `https://app.auqli.live/invite?ref=${referralCode}`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for older browsers
      const input = document.createElement('input');
      input.value = referralLink;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Shop on Auqli',
          text: `Use my referral link to shop on Auqli and save!`,
          url: referralLink,
        });
      } catch {
        // User cancelled share or share failed — fallback to copy
        handleCopy();
      }
    } else {
      handleCopy();
    }
  };

  const handleTrackOrder = () => navigate('/profile');
  const handleBackToHome = () => navigate('/shop');

  return (
    <div className="bg-white rounded-[10px] max-w-[510px] w-full p-[22px] sm:p-6 flex flex-col gap-5 shadow-sm">

      <div className="w-full">
        <img
          src="/congratulations-banner.svg"
          alt="Congratulations on your first order"
          className="w-full object-cover rounded-xl hidden sm:block"
          style={{ maxHeight: '198px' }}
        />
        <img
          src="/winner-banner.svg"
          alt="Winner — Congratulations on your first order"
          className="w-full object-cover rounded-xl sm:hidden"
          style={{ maxHeight: '180px' }}
        />
      </div>


      <div className="flex flex-col gap-5">

        <h2 className="font-lexend text-[18px] sm:text-[22px] font-medium leading-[28px] capitalize text-black text-center">
          You Saved ₦{rewardAmount.toLocaleString('en-NG')} Today.{' '}
          <span className="hidden sm:inline">Copy The Link And </span>
          Share Auqli To Earn More And Your Friend Saves Too
        </h2>


        <div className="flex items-center rounded-[10px] border border-black/50 overflow-hidden">
          <span className="flex-1 px-4 py-3 font-lexend text-[13px] sm:text-[14px] font-light text-black/80 truncate">
            {referralLink}
          </span>
          <button
            onClick={handleCopy}
            className="shrink-0 px-4 py-3 bg-black text-white font-lexend text-[14px] sm:text-[16px] font-medium capitalize hover:bg-black/80 transition-colors flex items-center gap-1.5"
            aria-label="Copy referral link"
          >
            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-3.5 h-3.5 sm:hidden" />}
            <span>{copied ? 'Copied!' : 'Copy'}</span>
          </button>
        </div>


        <div className="flex flex-col gap-3">

          <Button
            onClick={handleShare}
            className="sm:hidden w-full h-[50px] bg-[#328852] hover:bg-[#2a7345] text-white font-lexend text-[15px] font-normal capitalize rounded-md flex items-center justify-center gap-2"
          >
            <Share2 className="w-4 h-4" />
            Share Auqli
          </Button>


          <div className="flex gap-3">

            <Button
              onClick={handleTrackOrder}
              className="flex-1 h-[50px] bg-[#328852] hover:bg-[#2a7345] text-white font-lexend text-[15px] sm:text-[18px] font-normal capitalize rounded-md hidden sm:flex items-center justify-center"
            >
              Track Order
            </Button>


            <Button
              onClick={handleTrackOrder}
              variant="outline"
              className="flex-1 h-[50px] bg-white hover:bg-gray-50 text-black border border-gray-200 font-lexend text-[13px] font-normal capitalize rounded-md sm:hidden"
            >
              Track Order
            </Button>

            <Button
              onClick={handleBackToHome}
              variant="outline"
              className="flex-1 h-[50px] bg-white hover:bg-gray-50 text-black border border-gray-200 font-lexend text-[13px] sm:text-[18px] font-normal capitalize rounded-md"
            >
              Back To Home
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

